import React, { useEffect } from 'react';
import { IntlProvider } from 'react-intl';
import ReactNotification from 'react-notifications-component';
import { QueryClientProvider } from 'react-query';
import { Redirect, Route, Router, Switch } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';

import queryClient from './query-client';

import moment from 'moment';
import 'moment/locale/cs';
import 'moment/locale/sk';

import { Provider as ReduxProvider, useDispatch, useSelector } from 'react-redux';

import 'react-datepicker/dist/react-datepicker.css';
import 'react-notifications-component/dist/theme.css';

import GlobalStyle from './lib/theme/global';

import './index.css';
import cs from './locale/cs';
import en from './locale/en';
import sk from './locale/sk';

import { useAppSelector } from '@hooks/app/redux';
import useTokensResolver from '@hooks/app/useTokensResolver';
import { history } from '@lib/history';
import 'rc-picker/assets/index.css';
import GlobalModal from './components/global-modal';

import Loading from '@components/loading';
import ModalDesktop from '@components/modal-desktop';
import defaultTheme from '@lib/theme/defaultTheme';
import useCustomTheme from '@lib/theme/useCustomTheme';
import OfficeHours from '@pages/e-visits/office-hours';
import Provider from '@pages/provider';
import { ReactQueryDevtools } from 'react-query/devtools';
import 'strophe.js';
import 'strophejs-plugin-mam';
import 'strophejs-plugin-rsm';
import { ThemeProvider } from 'styled-components';
import { getLangFromUrl, getLangLocalStorage, setLangLocalStorage } from './helpers/utils-helpers';
import Layout from './layout/dashboard-layout';
import {
  Alerts,
  Chat,
  Communication,
  Home,
  LifeId,
  Login,
  MyProfile,
  NewsDetail,
  Patients,
  Receipts,
  Registration,
  ResetPasswordCode,
  ResetPasswordEmail,
  ResetPasswordNew,
  ResetPasswordStatus,
  Tags,
} from './pages';
import { EVisits } from './pages/e-visits';
import EVisitsEdit from './pages/e-visits/e-visits-edit';
import appStore from './store';
import { actions as appActions } from './store/ducks/app';
import { LANG_EN_SLUG, TLangSlug } from './types/app';
import TStore from './types/store';
import useEnvService from '@hooks/app/useEnvService';
// import * as types from 'styled-components/cssprop';

const locale: Record<TLangSlug, any> = {
  sk,
  en,
  cs,
};

const Routes = React.memo(() => {
  const isLogged: boolean = useAppSelector((store) => store.auth.isLogged);
  const isLoading: boolean = useAppSelector((store) => store.auth.isLoading);
  const xmppStatus: string = useAppSelector((store) => store.xmpp.status);
  useTokensResolver(true);

  if (isLoading || xmppStatus === 'CONNECTING') {
    return <Loading />;
  }

  if (isLogged) {
    return (
      <Layout>
        <Switch>
          <Route exact path="/" component={Home} />
          <Route exact path="/communication/:userId" component={Chat} />
          <Route exact path="/communication" component={Communication} />
          <Route path="/my-profile" component={MyProfile} />
          <Route path="/business" component={Provider} />
          <Route exact path="/alerts" component={Alerts} />
          <Route exact path="/patients" component={Patients} />
          <Route exact path="/news/:id" component={NewsDetail} />
          <Route exact path="/life-id/:userId/:tab?" component={LifeId} />
          <Route exact path="/receipts" component={Receipts} />
          <Route exact path="/office-hours/:workspaceId?" component={OfficeHours} />
          <Route exact path="/e-visits/calendar/:workspaceId?" component={EVisits} />
          <Route exact path="/tags" component={Tags} />
          <Route exact path="/e-visits/edit" component={EVisitsEdit} />
          <Redirect to="/" />
        </Switch>
      </Layout>
    );
  }

  return (
    <>
      <Switch>
        <Route exact path="/" component={Login} />
        <Route exact path="/registration/:step?" component={Registration} />
        <Route exact path="/reset-password" component={ResetPasswordEmail} />
        <Route exact path="/reset-password-code" component={ResetPasswordCode} />
        <Route exact path="/reset-password-new" component={ResetPasswordNew} />
        <Route exact path="/reset-password-status" component={ResetPasswordStatus} />
        <Redirect to="/" />
      </Switch>
    </>
  );
});

Routes.displayName = 'Routes';

const LangWrapper = React.memo(() => {
  const dispatch = useDispatch();
  const langSlug = useSelector((store: TStore) => store.app.currentLang);
  const [env] = useEnvService();

  // Resolve language
  useEffect(() => {
    const currentLang = getLangLocalStorage();
    if (currentLang) {
      dispatch(appActions.setLanguage({ language: currentLang }));
      moment.locale(currentLang);
    } else {
      moment.locale(LANG_EN_SLUG);
    }
  }, [dispatch]);

  const customTheme = useCustomTheme(defaultTheme);

  return (
    <IntlProvider locale={langSlug} messages={locale[langSlug]}>
      <ThemeProvider theme={customTheme}>
        <GlobalStyle />
        <Router history={history}>
          <QueryClientProvider client={queryClient}>
            <Routes />
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
          {!env.app.isCompact ? <ModalDesktop /> : null}
          <GlobalModal />
          <ReactNotification />
          <ReactTooltip />
        </Router>
      </ThemeProvider>
    </IntlProvider>
  );
});

LangWrapper.displayName = 'LangWrapper';

const App: React.FC = () => {
  if (!('indexedDB' in window)) {
    return <p>This browser is not supported</p>;
  }

  const urlLang = getLangFromUrl();

  if (urlLang) {
    setLangLocalStorage(urlLang);
  }

  return (
    <ReduxProvider store={appStore}>
      <LangWrapper />
    </ReduxProvider>
  );
};

export default App;
